import * as amplitude from "@amplitude/analytics-browser";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import Truck01Icon from "@redotech/redo-icons/truck-01.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { createWidget } from "@typeform/embed";
import "@typeform/embed/build/css/widget.css";
import { useEffect, useRef, useState } from "react";
import { OnboardingProps } from "../common";

const REDO_3PL_TYPEFORM_ID = "bde66s6n";
const REDO_3PL_INTEREST_SURVEY_SHOWN_EVENT = "redo-3pl-interest-survey-shown";
const REDO_3PL_INTEREST_SURVEY_DISMISSED_EVENT =
  "redo-3pl-interest-survey-dismissed";
const REDO_3PL_INTEREST_SURVEY_SUBMITTED_EVENT =
  "redo-3pl-interest-survey-submitted";

export function Redo3plInterestSurveyOnboarding({
  handleComplete,
  handleDismiss,
}: OnboardingProps) {
  const team = useRequiredContext(TeamContext);
  const user = useRequiredContext(UserContext);
  const submittedRef = useRef(false);

  useEffect(() => {
    amplitude.logEvent(
      REDO_3PL_INTEREST_SURVEY_SHOWN_EVENT,
      buildRedo3plInterestSurveyEventProperties({
        teamId: team._id.toString(),
        teamType: team.teamType,
        userId: user._id.toString(),
      }),
    );
  }, [team._id, team.teamType, user._id]);

  const handleClose = useHandler(() => {
    if (submittedRef.current) {
      return;
    }

    amplitude.logEvent(
      REDO_3PL_INTEREST_SURVEY_DISMISSED_EVENT,
      buildRedo3plInterestSurveyEventProperties({
        teamId: team._id.toString(),
        teamType: team.teamType,
        userId: user._id.toString(),
      }),
    );
    void handleDismiss();
  });

  const handleSubmit = useHandler(() => {
    submittedRef.current = true;
    amplitude.logEvent(
      REDO_3PL_INTEREST_SURVEY_SUBMITTED_EVENT,
      buildRedo3plInterestSurveyEventProperties({
        teamId: team._id.toString(),
        teamType: team.teamType,
        userId: user._id.toString(),
      }),
    );
    void handleComplete();
  });

  return (
    <Redo3plInterestSurveyModal onClose={handleClose} onSubmit={handleSubmit} />
  );
}

interface Redo3plInterestSurveyModalProps {
  onClose: () => void;
  onSubmit: () => void;
}

export function Redo3plInterestSurveyModal({
  onClose,
  onSubmit,
}: Redo3plInterestSurveyModalProps) {
  const team = useRequiredContext(TeamContext);
  const user = useRequiredContext(UserContext);
  const [typeformContainer, setTypeformContainer] =
    useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!typeformContainer) {
      return;
    }

    const widget = createWidget(REDO_3PL_TYPEFORM_ID, {
      container: typeformContainer,
      height: "100%",
      hidden: buildRedo3plTypeformMetadata({
        teamId: team._id.toString(),
        userId: user._id.toString(),
      }),
      iframeProps: { title: "Redo 3PL interest survey" },
      inlineOnMobile: true,
      onSubmit,
      width: "100%",
    });

    return () => {
      widget.unmount();
    };
  }, [onSubmit, team._id, typeformContainer, user._id]);

  return (
    <RedoModal
      headerBorder
      headerIconAlignment="inline"
      hideFooter
      isOpen
      modalSize="xl"
      noPadding
      onModalCloseRequested={onClose}
      subtitle="Tell us if Redo should help store, pick, pack, and ship orders for your brand."
      theme="success"
      title="Would you use Redo as your 3PL?"
      TitleIcon={Truck01Icon}
    >
      <Flex
        height="three-quarter-screen"
        minH="half-screen"
        ref={setTypeformContainer}
        w="full"
      />
    </RedoModal>
  );
}

function buildRedo3plInterestSurveyEventProperties({
  teamId,
  teamType,
  userId,
}: {
  teamId: string;
  teamType: string | undefined;
  userId: string;
}) {
  return {
    source: "feature-onboarding",
    team_id: teamId,
    team_type: teamType,
    user_id: userId,
  };
}

function buildRedo3plTypeformMetadata({
  teamId,
  userId,
}: {
  teamId: string;
  userId: string;
}) {
  return { team_id: teamId, user_id: userId };
}
